import { daysBetween, formatDate, formatMoney, isOverdue, isWithinDays } from '@/lib/format'
import type { Task, WeddingData } from '@/mock/types'
import type { LinkedModule } from '@/patterns/linked-record'

export type TaskFilter = 'all' | 'open' | 'done' | 'overdue' | 'upcoming'

export const taskFilters: TaskFilter[] = ['all', 'open', 'done', 'overdue', 'upcoming']

export const filterLabels: Record<TaskFilter, string> = {
  all: 'Vše',
  open: 'Zbývá',
  done: 'Hotovo',
  overdue: 'Po termínu',
  upcoming: 'Do 14 dnů',
}

export function parseFilter(value: unknown): TaskFilter {
  return taskFilters.includes(value as TaskFilter) ? (value as TaskFilter) : 'all'
}

/** Due within 14 days, today included; overdue tasks are counted separately. */
export function isUpcoming(task: Task) {
  return !task.done && isWithinDays(task.dueDate, 14)
}

export function isLate(task: Task) {
  return !task.done && isOverdue(task.dueDate)
}

/** The "Prioritní" group: high priority, overdue, or due within 14 days. */
export function isPriority(task: Task) {
  return !task.done && (task.priority === 'high' || isLate(task) || isUpcoming(task))
}

export function matchesFilter(task: Task, filter: TaskFilter) {
  switch (filter) {
    case 'open':
      return !task.done
    case 'done':
      return task.done
    case 'overdue':
      return isLate(task)
    case 'upcoming':
      return isUpcoming(task)
    default:
      return true
  }
}

export function matchesSearch(task: Task, query: string) {
  const needle = query.trim().toLocaleLowerCase('cs')
  if (!needle) return true
  return [task.title, task.note, ...task.labels].some((text) =>
    text.toLocaleLowerCase('cs').includes(needle),
  )
}

const priorityRank = { high: 0, normal: 1, low: 2 }

/** Soonest due first (undated last), then by priority. */
export function byDueThenPriority(a: Task, b: Task) {
  if (a.dueDate !== b.dueDate) {
    if (a.dueDate === null) return 1
    if (b.dueDate === null) return -1
    return daysBetween(b.dueDate, a.dueDate)
  }
  return priorityRank[a.priority] - priorityRank[b.priority]
}

export function automaticNotice(task: Task) {
  if (task.automatic === 'wedding-date') return 'Automaticky se splní po doplnění termínu svatby.'
  if (task.automatic === 'budget-total')
    return 'Automaticky se splní po nastavení celkové částky rozpočtu.'
  return null
}

/** The budget payment behind a payment task, read-only. */
export function taskPayment(task: Task, data: WeddingData) {
  if (!task.payment) return null
  const expense = data.budget.expenses.find((item) => item.id === task.payment?.expenseId)
  const payment = expense?.payments.find((item) => item.id === task.payment?.paymentId)
  if (!expense || !payment) return null
  const supplier = data.suppliers.find((item) => item.id === expense.supplierId)
  return {
    title: supplier?.name ?? expense.title,
    meta: [
      payment.name,
      formatMoney(payment.amount),
      payment.dueDate ? `splatnost ${formatDate(payment.dueDate)}` : null,
      payment.paid ? 'zaplaceno' : null,
    ]
      .filter(Boolean)
      .join(' · '),
  }
}

/** Where a task comes from: the short line in the list and the linked record in the detail. */
export function taskSource(
  task: Task,
  data: WeddingData,
): { module: LinkedModule; line: string; title: string; action: string } | null {
  const payment = taskPayment(task, data)
  if (payment)
    return {
      module: 'budget',
      line: `Rozpočet · ${payment.title}`,
      title: payment.title,
      action: 'Otevřít v rozpočtu',
    }
  switch (task.context?.type) {
    case 'venue':
      return {
        module: 'venue',
        line: `Místo · ${task.context.label}`,
        title: task.context.label,
        action: 'Otevřít místo',
      }
    case 'ceremony':
      return { module: 'ceremony', line: 'Obřad', title: 'Obřad', action: 'Otevřít obřad' }
    case 'reception':
      return {
        module: 'reception',
        line: 'Jídlo a pití',
        title: 'Jídlo a pití',
        action: 'Otevřít jídlo a pití',
      }
    case 'photo':
      return {
        module: 'photo',
        line: 'Foto a video',
        title: 'Foto a video',
        action: 'Otevřít foto a video',
      }
    case 'guests':
      return { module: 'guests', line: 'Hosté', title: 'Hosté', action: 'Otevřít hosty' }
    default:
      return null
  }
}

/** Every label used in the wedding, for the label picker. */
export function knownLabels(tasks: readonly Task[]) {
  return [...new Set(tasks.flatMap((task) => task.labels))].sort((a, b) => a.localeCompare(b, 'cs'))
}

export type TaskView = 'list' | 'timeline'

export function parseView(value: unknown): TaskView {
  return value === 'timeline' ? 'timeline' : 'list'
}

/** A stretch of the run-up to the wedding, counted back from the wedding date. */
export type Phase = {
  id: string
  label: string
  /** First day of the phase (inclusive); `null` = open-ended. */
  from: string | null
  /** Last day of the phase (inclusive); `null` = open-ended. */
  to: string | null
}

function shift(iso: string, { months = 0, days = 0 }: { months?: number; days?: number }) {
  const date = new Date(`${iso}T12:00:00`)
  date.setMonth(date.getMonth() + months)
  date.setDate(date.getDate() + days)
  return date.toISOString().slice(0, 10)
}

/**
 * The phases most planners use: more than a year out, 12–6, 6–3 and 3–1 months
 * before, the last month, the last week, the day itself and after the wedding.
 */
export function weddingPhases(weddingDate: string): Phase[] {
  const before = (months: number) => shift(weddingDate, { months: -months })
  const dayBefore = (iso: string) => shift(iso, { days: -1 })
  const weekBefore = shift(weddingDate, { days: -7 })
  return [
    { id: 'year', label: 'Víc než rok před svatbou', from: null, to: dayBefore(before(12)) },
    { id: '12-6', label: '12–6 měsíců před svatbou', from: before(12), to: dayBefore(before(6)) },
    { id: '6-3', label: '6–3 měsíce před svatbou', from: before(6), to: dayBefore(before(3)) },
    { id: '3-1', label: '3–1 měsíc před svatbou', from: before(3), to: dayBefore(before(1)) },
    { id: 'month', label: 'Poslední měsíc', from: before(1), to: dayBefore(weekBefore) },
    { id: 'week', label: 'Poslední týden', from: weekBefore, to: dayBefore(weddingDate) },
    { id: 'day', label: 'Svatební den', from: weddingDate, to: weddingDate },
    { id: 'after', label: 'Po svatbě', from: shift(weddingDate, { days: 1 }), to: null },
  ]
}

export function inPhase(iso: string, phase: Phase) {
  return (phase.from === null || iso >= phase.from) && (phase.to === null || iso <= phase.to)
}

/** "13. pro 2026 – 12. bře 2027", "do 11. čer 2026", "od 13. čer 2027". */
export function phaseRange(phase: Phase) {
  if (phase.from === phase.to && phase.from) return formatDate(phase.from)
  if (!phase.from && phase.to) return `do ${formatDate(phase.to)}`
  if (phase.from && !phase.to) return `od ${formatDate(phase.from)}`
  return `${formatDate(phase.from ?? '')} – ${formatDate(phase.to ?? '')}`
}
